/**
 * Global audio manager for Spotify previews
 * Ensures only one preview plays at a time across all components
 */

type AudioCallback = (isPlaying: boolean) => void;

class SpotifyAudioManager {
	private audio: HTMLAudioElement | null = null;
	private currentId: string | null = null;
	private callbacks: Map<string, AudioCallback> = new Map();

	play(id: string, url: string, onStateChange: AudioCallback): void {
		// Stop current audio if different
		if (this.currentId && this.currentId !== id) {
			this.stop();
		}

		// If same audio, toggle off
		if (this.currentId === id && this.audio) {
			this.stop();
			return;
		}

		// Create new audio
		this.audio = new Audio(url);
		this.audio.volume = 0.5;
		this.currentId = id;
		this.callbacks.set(id, onStateChange);

		this.audio.addEventListener("ended", () => {
			this.handleEnded();
		});

		this.audio.addEventListener("error", () => {
			this.handleEnded();
		});

		this.audio.play().then(() => {
			onStateChange(true);
		}).catch(() => {
			this.handleEnded();
		});
	}

	stop(): void {
		if (this.audio) {
			this.audio.pause();
			this.audio.currentTime = 0;
			this.audio = null;
		}

		if (this.currentId) {
			const callback = this.callbacks.get(this.currentId);
			if (callback) {
				callback(false);
			}
			this.callbacks.delete(this.currentId);
			this.currentId = null;
		}
	}

	private handleEnded(): void {
		if (this.currentId) {
			const callback = this.callbacks.get(this.currentId);
			if (callback) {
				callback(false);
			}
			this.callbacks.delete(this.currentId);
			this.currentId = null;
		}
		this.audio = null;
	}

	isPlaying(id: string): boolean {
		return this.currentId === id && this.audio !== null && !this.audio.paused;
	}
}

export const spotifyAudioManager = new SpotifyAudioManager();
